import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight, Search, X, Trash2, Copy } from "lucide-react";
import {
  listPrograms,
  createProgram,
  deleteProgram,
  cloneProgram,
} from "../../lib/api";
import { toast } from "sonner";
import CoachingTabs from "../../components/coaching/CoachingTabs";

const LEVELS = [
  { value: "", label: "—" },
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

// Classify a program by inspecting its title + level. Each program may belong to
// multiple distance buckets (e.g. "Marathon Post-Race Recovery" → recovery + marathon).
function classifyProgram(p) {
  const t = ((p.title || "") + " " + (p.goal || "")).toLowerCase();
  const tags = new Set();

  // Category — primary axis
  if (/\b(strength|s&c|s & c|mob \+ core|mob\+core|conditioning|ibex)\b/.test(t)) {
    tags.add("strength");
  }
  if (/\bpost[- ]?race|reverse[- ]?taper|recovery\b/.test(t) && !/aerobic base/.test(t)) {
    tags.add("recovery");
  }
  if (
    /\bmarathon|half|5k|10k|ultra|50k|100k|race prep|aerobic base|fastform|easy run|mileage|run\b/.test(t)
    && !tags.has("strength")
  ) {
    tags.add("running");
  }
  // If the title mentions both run AND strength/conditioning markers, mark as hybrid
  if (tags.has("strength") && /\b(run|tempo|long easy|hyrox|hybrid)\b/.test(t)) {
    tags.delete("strength");
    tags.add("hybrid");
  }
  if (tags.size === 0) tags.add("running"); // fallback

  // Distance / focus — secondary axis (a program can match multiple)
  if (/\b100k|hundred[- ]?k|ultra\b/.test(t)) tags.add("ultra");
  if (/\b50k|fifty[- ]?k\b/.test(t)) tags.add("ultra");
  if (/\bmarathon\b/.test(t) && !tags.has("ultra")) tags.add("marathon");
  if (/\bhalf\b/.test(t)) tags.add("half");
  if (/\b5k\/10k|5k|10k\b/.test(t)) tags.add("5k10k");
  if (/\baerobic base|distance[- ]?based|time[- ]?based|mileage\b/.test(t)) tags.add("base");
  if (/\bfastform|speed|form\b/.test(t)) tags.add("speed");

  return tags;
}

const CATEGORY_FILTERS = [
  { value: "all", label: "All" },
  { value: "running", label: "Running" },
  { value: "strength", label: "Strength" },
  { value: "hybrid", label: "Hybrid" },
  { value: "recovery", label: "Recovery" },
];

const FOCUS_FILTERS = [
  { value: "all", label: "All distances" },
  { value: "5k10k", label: "5K / 10K" },
  { value: "half", label: "Half marathon" },
  { value: "marathon", label: "Marathon" },
  { value: "ultra", label: "Ultra (50K+)" },
  { value: "base", label: "Aerobic base" },
  { value: "speed", label: "Speed / form" },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function CoachingLibraryPage() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    title: "",
    goal: "",
    experience_level: "",
    max_mileage: "",
    prerequisite: "",
    total_weeks: 8,
    start_day: "mon",
  });
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [focusFilter, setFocusFilter] = useState("all");
  const navigate = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listPrograms({ program_type: "generic" });
      setPrograms(data);
    } catch {
      toast.error("Couldn't load library programs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const q = searchTerm.trim().toLowerCase();
  // Pre-compute tag set per program once
  const programsWithTags = useMemo(
    () => programs.map((p) => ({ ...p, _tags: classifyProgram(p) })),
    [programs]
  );
  const filtered = useMemo(() => {
    return programsWithTags.filter((p) => {
      if (q) {
        const hit =
          (p.title || "").toLowerCase().includes(q) ||
          (p.goal || "").toLowerCase().includes(q) ||
          (p.experience_level || "").toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (categoryFilter !== "all" && !p._tags.has(categoryFilter)) return false;
      if (focusFilter !== "all" && !p._tags.has(focusFilter)) return false;
      return true;
    });
  }, [programsWithTags, q, categoryFilter, focusFilter]);

  const filtersActive =
    !!q || categoryFilter !== "all" || focusFilter !== "all";
  const clearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
    setFocusFilter("all");
  };

  // Counts per filter — show small numbers next to chips so coach knows what's available
  const categoryCounts = useMemo(() => {
    const counts = { all: programsWithTags.length };
    for (const f of CATEGORY_FILTERS) {
      if (f.value === "all") continue;
      counts[f.value] = programsWithTags.filter((p) => p._tags.has(f.value)).length;
    }
    return counts;
  }, [programsWithTags]);
  const focusCounts = useMemo(() => {
    const counts = { all: programsWithTags.length };
    for (const f of FOCUS_FILTERS) {
      if (f.value === "all") continue;
      counts[f.value] = programsWithTags.filter((p) => p._tags.has(f.value)).length;
    }
    return counts;
  }, [programsWithTags]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const p = await createProgram({
        program_type: "generic",
        athlete_id: null,
        title: form.title,
        goal: form.goal,
        experience_level: form.experience_level || null,
        max_mileage: form.max_mileage || null,
        prerequisite: form.prerequisite || null,
        total_weeks: Number(form.total_weeks) || 0,
        start_day: form.start_day,
      });
      toast.success("Library program created");
      navigate(`/coaching/programs/${p.id}/edit`);
    } catch {
      toast.error("Failed to create program");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (pid) => {
    if (!window.confirm("Delete this library program?")) return;
    await deleteProgram(pid);
    toast.success("Program deleted");
    refresh();
  };

  const onDuplicate = async (pid) => {
    try {
      const p = await cloneProgram(pid, {});
      toast.success("Program duplicated");
      navigate(`/coaching/programs/${p.id}/edit`);
    } catch {
      toast.error("Couldn't duplicate");
    }
  };

  return (
    <div
      className="max-w-7xl mx-auto px-6 lg:px-10 py-12"
      data-testid="coaching-library-page"
    >
      <CoachingTabs className="mb-8" />

      <section className="mb-10 border-b border-[var(--tir-ink)] pb-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-4">
              Coaching Studio · Program Library
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] text-[var(--tir-ink)]">
              Templates you can sell or reuse.
            </h1>
            <p className="text-base text-[var(--tir-muted)] mt-5 leading-relaxed max-w-xl">
              Build generic multi-week programs — then assign a copy to any 1:1 athlete
              with a single click. Edits to the 1:1 copy never touch the template.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowNew(true)}
              data-testid="new-library-program-btn"
              className="inline-flex items-center gap-2 bg-[var(--tir-burgundy)] text-white px-6 py-3 text-sm tracking-wider uppercase font-medium hover:bg-[var(--tir-burgundy-dark)] transition-colors"
            >
              <Plus size={16} /> New library program
            </button>
          </div>
        </div>
      </section>

      {showNew && (
        <form
          onSubmit={submit}
          className="mb-10 border border-[var(--tir-ink)] bg-white p-6"
          data-testid="new-library-program-form"
        >
          <div className="font-serif-display text-2xl mb-5">New library program</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Program title
              </label>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="library-title-input"
                placeholder="12-Week Half Marathon — Intermediate"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Goal
              </label>
              <input
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                placeholder="Finish strong at a half marathon"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Experience level
              </label>
              <select
                value={form.experience_level}
                onChange={(e) =>
                  setForm({ ...form, experience_level: e.target.value })
                }
                data-testid="library-level-select"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              >
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Peak weekly mileage
              </label>
              <input
                value={form.max_mileage}
                onChange={(e) => setForm({ ...form, max_mileage: e.target.value })}
                placeholder="55 km/week"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Prerequisite
              </label>
              <input
                value={form.prerequisite}
                onChange={(e) => setForm({ ...form, prerequisite: e.target.value })}
                placeholder="Comfortable running 30 km/week"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Total weeks
              </label>
              <input
                type="number"
                min="1"
                max="52"
                value={form.total_weeks}
                onChange={(e) => setForm({ ...form, total_weeks: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none font-mono-tir"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Week starts on
              </label>
              <select
                value={form.start_day}
                onChange={(e) => setForm({ ...form, start_day: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 text-base focus:outline-none"
              >
                <option value="mon">Monday (Mon–Sun)</option>
                <option value="sun">Sunday (Sun–Sat)</option>
                <option value="day1">Day 1 – Day 7</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-library-program-btn"
              className="bg-[var(--tir-burgundy)] text-white px-6 py-2.5 text-sm tracking-wider uppercase font-medium hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
            >
              {submitting ? "Creating…" : "Create program"}
            </button>
            <button
              type="button"
              onClick={() => setShowNew(false)}
              className="text-sm tracking-wider uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <section>
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-serif-display text-3xl">Library</h2>
          <span className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)]">
            {filtersActive ? `${filtered.length} of ${programs.length}` : `${programs.length} total`}
          </span>
        </div>

        {programs.length > 0 && (
          <div className="space-y-4 mb-6" data-testid="library-filters">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-0 top-1/2 -translate-y-1/2 text-[var(--tir-muted)] pointer-events-none"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, goal, or level"
                data-testid="library-search-input"
                className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] pl-7 pr-8 py-2.5 text-base focus:outline-none"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[var(--tir-muted)] hover:text-[var(--tir-ink)] p-1"
                  aria-label="Clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Category chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mr-1 hidden sm:inline">
                Type
              </span>
              {CATEGORY_FILTERS.map((f) => {
                const isActive = categoryFilter === f.value;
                const count = categoryCounts[f.value];
                if (count === 0 && f.value !== "all") return null;
                return (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setCategoryFilter(f.value)}
                    data-testid={`filter-cat-${f.value}`}
                    className={`px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold border transition-colors inline-flex items-center gap-1.5 ${
                      isActive
                        ? "bg-[var(--tir-burgundy)] text-white border-[var(--tir-burgundy)]"
                        : "bg-white border-[var(--tir-border)] text-[var(--tir-ink)] hover:border-[var(--tir-ink)]"
                    }`}
                  >
                    <span>{f.label}</span>
                    <span className={`text-[9px] font-mono-tir ${isActive ? "opacity-80" : "text-[var(--tir-muted)]"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Focus chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mr-1 hidden sm:inline">
                Focus
              </span>
              {FOCUS_FILTERS.map((f) => {
                const isActive = focusFilter === f.value;
                const count = focusCounts[f.value];
                if (count === 0 && f.value !== "all") return null;
                return (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFocusFilter(f.value)}
                    data-testid={`filter-focus-${f.value}`}
                    className={`px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold border transition-colors inline-flex items-center gap-1.5 ${
                      isActive
                        ? "bg-[var(--tir-navy)] text-white border-[var(--tir-navy)]"
                        : "bg-white border-[var(--tir-border)] text-[var(--tir-ink)] hover:border-[var(--tir-ink)]"
                    }`}
                  >
                    <span>{f.label}</span>
                    <span className={`text-[9px] font-mono-tir ${isActive ? "opacity-80" : "text-[var(--tir-muted)]"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
              {filtersActive && (
                <button
                  type="button"
                  onClick={clearFilters}
                  data-testid="filter-clear"
                  className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] inline-flex items-center gap-1 ml-auto"
                >
                  <X size={11} /> Clear filters
                </button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-[var(--tir-muted)] py-12 text-center">Loading…</div>
        ) : programs.length === 0 ? (
          <div className="border border-dashed border-[var(--tir-border)] py-16 text-center">
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">
              No library programs yet
            </div>
            <div className="text-sm text-[var(--tir-muted)] mt-2">
              Build a reusable template you can assign to any athlete.
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[var(--tir-border)] py-12 text-center">
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">
              No matches
            </div>
            <div className="text-sm text-[var(--tir-muted)] mt-2">
              Try a different search or clear your filters.
            </div>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-4 inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
            >
              <X size={12} /> Clear filters
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-[var(--tir-border)] border-t border-b border-[var(--tir-border)]">
            {filtered.map((p) => (
              <li
                key={p.id}
                className="grid grid-cols-12 gap-4 py-5 px-2 items-center group"
                data-testid={`library-program-row-${p.id}`}
              >
                <Link
                  to={`/coaching/programs/${p.id}/edit`}
                  className="col-span-12 md:col-span-6 block"
                >
                  <div className="font-serif-display text-2xl text-[var(--tir-ink)] group-hover:text-[var(--tir-burgundy)] transition-colors">
                    {p.title}
                  </div>
                  <div className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] mt-1 flex flex-wrap gap-x-3">
                    <span>{p.total_weeks} weeks</span>
                    {p.experience_level && (
                      <span className="capitalize">{p.experience_level}</span>
                    )}
                    {p.max_mileage && <span>Peak {p.max_mileage}</span>}
                  </div>
                  {p.goal && (
                    <div className="text-sm text-[var(--tir-muted)] mt-1 truncate">
                      {p.goal}
                    </div>
                  )}
                </Link>
                <div className="col-span-6 md:col-span-3 text-sm text-[var(--tir-muted)]">
                  <div className="text-[10px] tracking-[0.22em] uppercase">Updated</div>
                  <div>{formatDate(p.updated_at)}</div>
                </div>
                <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-4">
                  <button
                    onClick={() => onDuplicate(p.id)}
                    data-testid={`duplicate-library-${p.id}`}
                    className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
                  >
                    <Copy size={13} /> Duplicate
                  </button>
                  <Link
                    to={`/coaching/programs/${p.id}/edit`}
                    className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
                  >
                    Open <ArrowUpRight size={13} />
                  </Link>
                  <button
                    onClick={() => onDelete(p.id)}
                    className="text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
                    aria-label="Delete program"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
